import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  NbButtonModule,
  NbCardModule,
  NbDialogService,
  NbIconModule,
  NbSpinnerModule,
  NbToastrService,
} from '@nebular/theme';
import { Subscription, timer } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { ExecucaoRotinaDto, RotinaIntegracaoDto } from '../../core/api/integracao.dto';
import { DialogAlertComponent } from '../inscricao/inscricao.dialog.component';
import { IntegracaoService } from '../../core/api/integracao.service';

import { mensagemDeErro } from '../../utils/mensagem-erro';
/** De quanto em quanto tempo a lista é relida enquanto houver rotina rodando. */
const INTERVALO_POLLING_MS = 5000;

/**
 * Painel de integrações com o Theos (somente admin).
 *
 * Pontos de chamada (contrato igreja-api):
 * - `GET  /integracoes/rotinas`
 * - `POST /integracoes/rotinas/{codigo}/executar`  (202 disparada · 409 já em execução)
 * - `POST /integracoes/rotinas/executar-todas`
 */
@Component({
  selector: 'app-integracoes',
  standalone: true,
  imports: [CommonModule, NbCardModule, NbButtonModule, NbIconModule, NbSpinnerModule],
  templateUrl: './integracoes.component.html',
  styleUrl: './integracoes.component.scss',
})
export class IntegracoesComponent implements OnInit, OnDestroy {
  rotinas: RotinaIntegracaoDto[] = [];
  carregando = false;
  erro: string | null = null;

  /** Código da rotina cujo disparo está em voo (feedback do botão). */
  disparando: string | null = null;
  disparandoTodas = false;

  private polling?: Subscription;

  constructor(
    private integracaoService: IntegracaoService,
    private dialogService: NbDialogService,
    private toastr: NbToastrService,
  ) {}

  ngOnInit(): void {
    this.carregar();
  }

  ngOnDestroy(): void {
    this.polling?.unsubscribe();
  }

  carregar(silencioso = false): void {
    if (!silencioso) {
      this.carregando = true;
      this.erro = null;
    }
    this.integracaoService
      .listarRotinas()
      .pipe(finalize(() => (this.carregando = false)))
      .subscribe({
        next: (lista) => {
          this.rotinas = lista;
          this.agendarProximaLeitura();
        },
        error: (err) => {
          if (!silencioso) {
            this.erro = mensagemDeErro(err, 'Não foi possível carregar as rotinas de integração.');
          }
        },
      });
  }

  get algumaEmExecucao(): boolean {
    return this.rotinas.some((r) => r.emExecucao === true);
  }

  /** Enquanto houver rotina rodando, relê a lista periodicamente para atualizar o estado. */
  private agendarProximaLeitura(): void {
    this.polling?.unsubscribe();
    if (!this.algumaEmExecucao) return;
    this.polling = timer(INTERVALO_POLLING_MS).subscribe(() => this.carregar(true));
  }

  executar(rotina: RotinaIntegracaoDto): void {
    if (this.disparando || this.disparandoTodas || rotina.emExecucao) return;

    this.dialogService
      .open(DialogAlertComponent, {
        context: {
          title: rotina.nome,
          message: `${rotina.descricao ?? ''} A rotina roda em segundo plano e altera dados no Theos e na base local. Confirma o disparo agora?`,
          mostrarConfirmacao: true,
          msgConfirmacao: 'Sim, executar agora',
        },
      })
      .onClose.subscribe((ok: boolean) => {
        if (!ok) return;
        this.disparando = rotina.codigo;
        this.integracaoService
          .executar(rotina.codigo)
          .pipe(finalize(() => (this.disparando = null)))
          .subscribe({
            next: (execucao) => {
              this.relatarExecucao(execucao, rotina.nome);
              this.carregar(true);
            },
            error: (err) => this.tratarErroDisparo(err, rotina.nome),
          });
      });
  }

  executarTodas(): void {
    if (this.disparando || this.disparandoTodas) return;

    this.dialogService
      .open(DialogAlertComponent, {
        context: {
          title: 'Executar todas as integrações',
          message:
            `As ${this.rotinas.length} rotinas serão disparadas em sequência, em segundo plano — ` +
            'sincronização de pessoas, importação de catequizandos, download dos dados e ' +
            'conciliação de turmas/matrículas. Isso altera dados no Theos e na base local e pode ' +
            'demorar. Confirma?',
          mostrarConfirmacao: true,
          msgConfirmacao: 'Sim, executar todas',
        },
      })
      .onClose.subscribe((ok: boolean) => {
        if (!ok) return;
        this.disparandoTodas = true;
        this.integracaoService
          .executarTodas()
          .pipe(finalize(() => (this.disparandoTodas = false)))
          .subscribe({
            next: (execucoes) => {
              const jaRodando = execucoes.filter((e) => e.status === 'JA_EM_EXECUCAO').length;
              const disparadas = execucoes.length - jaRodando;
              this.toastr.success(
                `${disparadas} rotina(s) disparada(s) em segundo plano` +
                  (jaRodando ? ` — ${jaRodando} já estava(m) rodando.` : '.'),
                'Integrações',
              );
              this.carregar(true);
            },
            error: (err) =>
              this.toastr.danger(
                mensagemDeErro(err, 'Não foi possível disparar as integrações.'),
                'Erro',
              ),
          });
      });
  }

  private relatarExecucao(execucao: ExecucaoRotinaDto, nome: string): void {
    switch (execucao?.status) {
      case 'INICIADA':
        this.toastr.success(`${nome} foi disparada e roda em segundo plano.`, 'Integração iniciada');
        break;
      case 'CONCLUIDA':
        this.toastr.success(
          `${nome} concluída — ${execucao.qtdRegistros ?? 0} registro(s).`,
          'Integração concluída',
        );
        break;
      case 'FALHA':
        this.toastr.danger(execucao.mensagemErro ?? `${nome} terminou com erro.`, 'Falha na integração');
        break;
      case 'JA_EM_EXECUCAO':
        this.toastr.warning(`${nome} já está em execução.`, 'Integração');
        break;
      default:
        this.toastr.info(`${nome}: disparo registrado.`, 'Integração');
    }
  }

  /** 409 traz o próprio `ExecucaoRotinaDto` no corpo (rotina já rodando). */
  private tratarErroDisparo(err: unknown, nome: string): void {
    const e = err as { status?: number; error?: ExecucaoRotinaDto } | null;
    if (e?.status === 409) {
      this.toastr.warning(`${nome} já está em execução.`, 'Integração');
      this.carregar(true);
      return;
    }
    this.toastr.danger(mensagemDeErro(err, `Não foi possível executar ${nome}.`), 'Erro');
  }

  dataHoraUltima(rotina: RotinaIntegracaoDto): Date | null {
    const iso = rotina.ultimaExecucao?.dataHora;
    if (!iso) return null;
    const d = new Date(iso);
    return isNaN(d.getTime()) ? null : d;
  }

  trackByCodigo = (_: number, r: RotinaIntegracaoDto): string => r.codigo;

}
